import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RestoreIcon from '@mui/icons-material/Restore';
import ArchiveIcon from '@mui/icons-material/Archive';
import SearchIcon from '@mui/icons-material/Search';
import ConstructionIcon from '@mui/icons-material/Construction';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, doc, setDoc, updateDoc, query, where, getDocs, arrayUnion, getDoc, deleteDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useCategories } from '../contexts/CategoriesContext';
import { alpha } from '@mui/material/styles';
import { PageContent, COMMON_STYLES } from '../theme';

const HotelManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { categories } = useCategories();
  
  const [currentTab, setCurrentTab] = useState(0); // 0: active, 1: work_in_progress, 2: archived
  const [hotels, setHotels] = useState([]);
  const [filteredHotels, setFilteredHotels] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [hotelCounts, setHotelCounts] = useState({ active: 0, archived: 0, work_in_progress: 0 });
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [newHotel, setNewHotel] = useState({
    propertyNumber: '',
    categoryData: {},
    status: 'work_in_progress',
    expectedOpeningDate: '',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [openEditDateDialog, setOpenEditDateDialog] = useState(false);
  const [selectedHotelForEdit, setSelectedHotelForEdit] = useState(null);
  const [editedDate, setEditedDate] = useState('');
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [hotelToDelete, setHotelToDelete] = useState(null);

  // Reset new hotel data when dialog opens/closes
  useEffect(() => {
    if (!openAddDialog) {
      setNewHotel({
        propertyNumber: '',
        categoryData: {},
        status: 'work_in_progress',
        expectedOpeningDate: '',
      });
    }
  }, [openAddDialog]);

  const handleCategoryChange = (fieldName, value) => {
    setNewHotel(prev => ({
      ...prev,
      categoryData: {
        ...prev.categoryData,
        [`category_${fieldName.toLowerCase()}`]: {
          value: value,
          updatedAt: new Date(),
          updatedBy: user.email
        }
      }
    }));
  };

  // Status configuration
  const STATUS_CONFIG = {
    active: {
      label: 'Active',
      color: 'success',
      icon: null
    },
    work_in_progress: {
      label: 'Work In Progress',
      color: 'info',
      icon: <ConstructionIcon fontSize="small" />
    },
    archived: {
      label: 'Archived',
      color: 'error',
      icon: <ArchiveIcon fontSize="small" />
    }
  };

  // Load total counts
  useEffect(() => {
    if (!user) return;

    const loadCounts = async () => {
      try {
        console.log('Fetching hotel counts for all statuses');
        // Use Promise.all to fetch counts in parallel
        const countPromises = ['active', 'archived', 'work_in_progress'].map(async (status) => {
          const q = query(
            collection(db, 'hotels'),
            where('status', '==', status)
          );
          const snapshot = await getDocs(q);
          return { status, count: snapshot.size };
        });
        
        const results = await Promise.all(countPromises);
        const counts = {};
        results.forEach(({ status, count }) => {
          counts[status] = count;
        });
        setHotelCounts(counts);
      } catch (error) {
        console.error('Error loading counts:', error);
      }
    };

    loadCounts();
  }, [user]);

  // Load hotels based on status - Replace onSnapshot with getDocs
  useEffect(() => {
    if (!user) return;
    setLoading(true);

    const fetchHotels = async () => {
      try {
        const statusMap = ['active', 'work_in_progress', 'archived'];
        const currentStatus = statusMap[currentTab];

        console.log('Loading hotels with status:', currentStatus);

        const q = query(
          collection(db, 'hotels'),
          where('status', '==', currentStatus)
        );

        console.log('Query created with status:', currentStatus);
        
        const snapshot = await getDocs(q);
        console.log('Query executed, docs count:', snapshot.docs.length);
        
        const hotelData = snapshot.docs.map(doc => ({
          id: doc.id,
          propertyNumber: doc.data().categoryData?.category_propertynumber?.value || doc.id,
          ...doc.data()
        }));

        // Sort hotels by property number
        const sortedHotels = hotelData.sort((a, b) => {
          const propA = a.categoryData?.category_propertynumber?.value || '';
          const propB = b.categoryData?.category_propertynumber?.value || '';
          return propA.localeCompare(propB);
        });

        console.log('Processed and sorted hotel data:', sortedHotels);
        setHotels(sortedHotels);
      } catch (error) {
        console.error('Error fetching hotels:', error);
        setSnackbar({
          open: true,
          message: 'Error loading hotels: ' + error.message,
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchHotels();
  }, [user, currentTab]);

  // Add search filter effect
  useEffect(() => {
    if (!hotels) return;
    
    const filtered = hotels.filter(hotel => {
      const searchLower = searchQuery.toLowerCase();
      const propertyNumber = hotel.categoryData?.category_propertynumber?.value?.toLowerCase() || '';
      const name = hotel.categoryData?.category_name?.value?.toLowerCase() || '';
      
      return propertyNumber.includes(searchLower) || name.includes(searchLower);
    });
    
    setFilteredHotels(filtered);
  }, [searchQuery, hotels]);

  const handleAddHotel = async () => {
    try {
      if (!newHotel.propertyNumber) {
        setSnackbar({
          open: true,
          message: 'Property number is required',
          severity: 'error'
        });
        return;
      }

      const propertyNumber = newHotel.propertyNumber.toLowerCase(); // Convert to lowercase for database ID

      // Check if property number already exists
      const hotelRef = doc(db, 'hotels', propertyNumber);
      const hotelDoc = await getDoc(hotelRef);
      
      if (hotelDoc.exists()) {
        setSnackbar({
          open: true,
          message: 'A hotel with this property number already exists',
          severity: 'error'
        });
        return;
      }

      const newHotelData = {
        status: newHotel.status,
        statusHistory: [{
          status: newHotel.status,
          timestamp: new Date(),
          updatedBy: user.email
        }],
        createdAt: new Date(),
        createdBy: user.email,
        categoryData: {
          ...newHotel.categoryData,
          category_propertynumber: {
            value: newHotel.propertyNumber.toUpperCase(), // Store display value in uppercase
            updatedAt: new Date(),
            updatedBy: user.email
          }
        },
        expectedOpeningDate: newHotel.expectedOpeningDate,
      };

      console.log('Adding new hotel with data:', newHotelData);

      // Create new hotel document
      await setDoc(hotelRef, newHotelData);

      console.log('Hotel added successfully');

      setOpenAddDialog(false);
      setNewHotel({
        propertyNumber: '',
        categoryData: {},
        status: 'active',
        expectedOpeningDate: '',
      });
      setSnackbar({
        open: true,
        message: 'Hotel added successfully',
        severity: 'success'
      });

      // Refresh counts and current hotel list after adding new hotel
      const loadCounts = async () => {
        try {
          // Use Promise.all to fetch counts in parallel
          const countPromises = ['active', 'archived', 'work_in_progress'].map(async (status) => {
            const q = query(
              collection(db, 'hotels'),
              where('status', '==', status)
            );
            const snapshot = await getDocs(q);
            return { status, count: snapshot.size };
          });
          
          const results = await Promise.all(countPromises);
          const counts = {};
          results.forEach(({ status, count }) => {
            counts[status] = count;
          });
          setHotelCounts(counts);
          
          // Also refresh the current tab's hotel list if the new hotel would appear there
          if (newHotel.status === ['active', 'work_in_progress', 'archived'][currentTab]) {
            const statusMap = ['active', 'work_in_progress', 'archived'];
            const currentStatus = statusMap[currentTab];
            
            const q = query(
              collection(db, 'hotels'),
              where('status', '==', currentStatus)
            );
            
            const snapshot = await getDocs(q);
            const hotelData = snapshot.docs.map(doc => ({
              id: doc.id,
              propertyNumber: doc.data().categoryData?.category_propertynumber?.value || doc.id,
              ...doc.data()
            }));
            
            const sortedHotels = hotelData.sort((a, b) => {
              const propA = a.categoryData?.category_propertynumber?.value || '';
              const propB = b.categoryData?.category_propertynumber?.value || '';
              return propA.localeCompare(propB);
            });
            
            setHotels(sortedHotels);
          }
        } catch (error) {
          console.error('Error refreshing data after adding hotel:', error);
        }
      };
      
      loadCounts();

    } catch (error) {
      console.error('Error adding hotel:', error);
      setSnackbar({
        open: true,
        message: 'Error adding hotel: ' + error.message,
        severity: 'error'
      });
    }
  };

  const handleStatusChange = async (hotelId, newStatus) => {
    try {
      console.log('Attempting to update hotel status:', { hotelId, newStatus });
      
      // First check if the hotel exists by ID
      let hotelRef = doc(db, 'hotels', hotelId.toLowerCase());
      let hotelDoc = await getDoc(hotelRef);
      
      console.log('Initial hotel document check:', { exists: hotelDoc.exists() });
      
      if (!hotelDoc.exists()) {
        console.log('Hotel not found by ID, searching by property number...');
        // Try multiple queries to find the hotel
        const queries = [
          // Try exact match (lowercase)
          query(
            collection(db, 'hotels'),
            where('categoryData.category_propertynumber.value', '==', hotelId.toLowerCase())
          ),
          // Try exact match (original case)
          query(
            collection(db, 'hotels'),
            where('categoryData.category_propertynumber.value', '==', hotelId)
          ),
          // Try by propertyNumber field
          query(
            collection(db, 'hotels'),
            where('propertyNumber', '==', hotelId.toLowerCase())
          )
        ];

        for (const q of queries) {
          const querySnapshot = await getDocs(q);
          console.log('Query results:', { count: querySnapshot.size });
          
          if (!querySnapshot.empty) {
            const actualHotelDoc = querySnapshot.docs[0];
            console.log('Found hotel:', { 
              id: actualHotelDoc.id, 
              propertyNumber: actualHotelDoc.data().propertyNumber,
              categoryPropertyNumber: actualHotelDoc.data()?.categoryData?.category_propertynumber?.value 
            });
            hotelRef = doc(db, 'hotels', actualHotelDoc.id);
            hotelDoc = actualHotelDoc;
            break;
          }
        }

        if (!hotelDoc || !hotelDoc.exists()) {
          console.error('Hotel not found after all queries');
          throw new Error('Hotel not found');
        }
      }
      
      console.log('Updating hotel document:', { 
        id: hotelDoc.id, 
        currentStatus: hotelDoc.data().status,
        newStatus: newStatus 
      });

      await updateDoc(hotelRef, {
        status: newStatus,
        statusHistory: arrayUnion({
          status: newStatus,
          timestamp: new Date(),
          updatedBy: user.email
        })
      });

      // Refresh counts after status change
      const loadCounts = async () => {
        try {
          // Use Promise.all to fetch counts in parallel
          const countPromises = ['active', 'archived', 'work_in_progress'].map(async (status) => {
            const q = query(
              collection(db, 'hotels'),
              where('status', '==', status)
            );
            const snapshot = await getDocs(q);
            return { status, count: snapshot.size };
          });
          
          const results = await Promise.all(countPromises);
          const counts = {};
          results.forEach(({ status, count }) => {
            counts[status] = count;
          });
          setHotelCounts(counts);
          
          // Also refresh the current list if needed
          const statusMap = ['active', 'work_in_progress', 'archived'];
          const currentStatus = statusMap[currentTab];
          if (currentStatus === newStatus || hotelDoc.data().status === currentStatus) {
            // Refresh current tab data
            const q = query(
              collection(db, 'hotels'),
              where('status', '==', currentStatus)
            );
            
            const snapshot = await getDocs(q);
            const hotelData = snapshot.docs.map(doc => ({
              id: doc.id,
              propertyNumber: doc.data().categoryData?.category_propertynumber?.value || doc.id,
              ...doc.data()
            }));
            
            const sortedHotels = hotelData.sort((a, b) => {
              const propA = a.categoryData?.category_propertynumber?.value || '';
              const propB = b.categoryData?.category_propertynumber?.value || '';
              return propA.localeCompare(propB);
            });
            
            setHotels(sortedHotels);
          }
        } catch (error) {
          console.error('Error refreshing data after status change:', error);
        }
      };
      loadCounts();

      setSnackbar({
        open: true,
        message: `Hotel ${
          newStatus === 'archived' ? 'archived' : 
          newStatus === 'work_in_progress' ? 'set to Work In Progress' :
          'activated'
        } successfully`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating hotel status:', error);
      setSnackbar({
        open: true,
        message: 'Error updating hotel status: ' + error.message,
        severity: 'error'
      });
    }
  };

  const handleUpdateExpectedOpeningDate = async () => {
    try {
      if (!selectedHotelForEdit) return;

      const hotelRef = doc(db, 'hotels', selectedHotelForEdit.id);
      await updateDoc(hotelRef, {
        expectedOpeningDate: editedDate
      });

      setSnackbar({
        open: true,
        message: 'Expected opening date updated successfully',
        severity: 'success'
      });
      setOpenEditDateDialog(false);
      setSelectedHotelForEdit(null);
      setEditedDate('');
    } catch (error) {
      console.error('Error updating expected opening date:', error);
      setSnackbar({
        open: true,
        message: 'Error updating expected opening date: ' + error.message,
        severity: 'error'
      });
    }
  };

  const handleDeleteHotel = async () => {
    try {
      if (!hotelToDelete) return;

      console.log('Attempting to delete hotel:', hotelToDelete.propertyNumber);

      // Find the hotel document
      let hotelRef = doc(db, 'hotels', hotelToDelete.id);
      let hotelDoc = await getDoc(hotelRef);

      if (!hotelDoc.exists()) {
        // Try to find by property number
        const q = query(
          collection(db, 'hotels'),
          where('categoryData.category_propertynumber.value', '==', hotelToDelete.propertyNumber.toLowerCase())
        );
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          hotelRef = doc(db, 'hotels', querySnapshot.docs[0].id);
        } else {
          throw new Error('Hotel not found');
        }
      }

      // Delete the hotel document
      await deleteDoc(hotelRef);

      // Refresh the hotel list and counts
      const statusMap = ['active', 'work_in_progress', 'archived'];
      const currentStatus = statusMap[currentTab];
      
      const q = query(
        collection(db, 'hotels'),
        where('status', '==', currentStatus)
      );
      
      const snapshot = await getDocs(q);
      const hotelData = snapshot.docs.map(doc => ({
        id: doc.id,
        propertyNumber: doc.data().categoryData?.category_propertynumber?.value || doc.id,
        ...doc.data()
      }));
      
      const sortedHotels = hotelData.sort((a, b) => {
        const propA = a.categoryData?.category_propertynumber?.value || '';
        const propB = b.categoryData?.category_propertynumber?.value || '';
        return propA.localeCompare(propB);
      });
      
      setHotels(sortedHotels);

      // Refresh counts
      const countPromises = ['active', 'archived', 'work_in_progress'].map(async (status) => {
        const q = query(
          collection(db, 'hotels'),
          where('status', '==', status)
        );
        const snapshot = await getDocs(q);
        return { status, count: snapshot.size };
      });
      
      const results = await Promise.all(countPromises);
      const counts = {};
      results.forEach(({ status, count }) => {
        counts[status] = count;
      });
      setHotelCounts(counts);

      setSnackbar({
        open: true,
        message: `Hotel "${hotelToDelete.propertyNumber}" permanently deleted`,
        severity: 'success'
      });

      setOpenDeleteDialog(false);
      setHotelToDelete(null);
    } catch (error) {
      console.error('Error deleting hotel:', error);
      setSnackbar({
        open: true,
        message: 'Error deleting hotel: ' + error.message,
        severity: 'error'
      });
    }
  };

  // Add handler for rows per page change
  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when changing rows per page
  };

  // Function to refresh data manually
  const refreshData = async () => {
    if (!user || isRefreshing) return;
    
    setIsRefreshing(true);
    try {
      console.log('Manually refreshing data');
      
      // Refresh counts
      const countPromises = ['active', 'archived', 'work_in_progress'].map(async (status) => {
        const q = query(
          collection(db, 'hotels'),
          where('status', '==', status)
        );
        const snapshot = await getDocs(q);
        return { status, count: snapshot.size };
      });
      
      const results = await Promise.all(countPromises);
      const counts = {};
      results.forEach(({ status, count }) => {
        counts[status] = count;
      });
      setHotelCounts(counts);
      
      // Refresh current tab hotels
      const statusMap = ['active', 'work_in_progress', 'archived'];
      const currentStatus = statusMap[currentTab];
      
      const q = query(
        collection(db, 'hotels'),
        where('status', '==', currentStatus)
      );
      
      const snapshot = await getDocs(q);
      const hotelData = snapshot.docs.map(doc => ({
        id: doc.id,
        propertyNumber: doc.data().categoryData?.category_propertynumber?.value || doc.id,
        ...doc.data()
      }));
      
      const sortedHotels = hotelData.sort((a, b) => {
        const propA = a.categoryData?.category_propertynumber?.value || '';
        const propB = b.categoryData?.category_propertynumber?.value || '';
        return propA.localeCompare(propB);
      });
      
      setHotels(sortedHotels);
      
      setSnackbar({
        open: true,
        message: 'Data refreshed successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error refreshing data:', error);
      setSnackbar({
        open: true,
        message: 'Error refreshing data: ' + error.message,
        severity: 'error'
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <PageContent>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', md: 'center' },
        gap: 2,
        mb: 4,
        width: '100%'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton 
            onClick={() => navigate('/')} 
            sx={{
              color: 'text.secondary',
              bgcolor: theme => alpha(theme.palette.primary.main, 0.04),
              '&:hover': {
                color: 'primary.main',
                bgcolor: theme => alpha(theme.palette.primary.main, 0.08)
              },
              borderRadius: 2
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography 
              variant="h4" 
              sx={{ 
                fontFamily: 'StrawberrySansDisplay',
                fontWeight: 400,
                mb: 1,
                color: 'text.primary',
                fontSize: { xs: '1.5rem', md: '2rem' },
                letterSpacing: '-0.02em'
              }}
            >
              Hotel Management
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ 
                fontSize: { xs: '0.875rem', md: '1rem' } 
              }}
            >
              Manage hotels in the system
            </Typography>
          </Box>
        </Box>

        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          flexDirection: { xs: 'column', sm: 'row' },
          width: { xs: '100%', md: 'auto' }
        }}>
          <TextField
            placeholder="Search hotels..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ 
              flexGrow: 1,
              minWidth: { sm: 240 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: 'background.paper'
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton 
                    size="small" 
                    onClick={() => setSearchQuery('')}
                    sx={{ color: 'text.secondary' }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          
          <Tooltip title="Refresh data">
            <span>
              <IconButton 
                onClick={refreshData}
                disabled={isRefreshing}
                sx={{
                  bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: theme => alpha(theme.palette.primary.main, 0.12),
                  },
                  height: 40,
                  width: 40
                }}
              >
                <RefreshIcon sx={{ 
                  animation: isRefreshing ? 'spin 1s linear infinite' : 'none'
                }} />
              </IconButton>
            </span>
          </Tooltip>
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddDialog(true)}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              py: 1,
              boxShadow: 'none',
              whiteSpace: 'nowrap',
              '&:hover': {
                boxShadow: 'none',
                bgcolor: theme => alpha(theme.palette.primary.main, 0.8)
              }
            }}
          >
            Add Hotel
          </Button>
        </Box>
      </Box>

      {/* Tabs and Table Container */}
      <Card sx={{ ...COMMON_STYLES.card }}>
        <Box sx={{ overflow: 'visible', width: '100%' }}>
        <Tabs 
          value={currentTab} 
          onChange={(e, newValue) => setCurrentTab(newValue)}
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            px: 3,
            '& .MuiTab-root': {
              fontFamily: 'StrawberrySansText',
              textTransform: 'none',
              fontSize: '0.875rem',
              minHeight: 48,
              fontWeight: 500,
              color: 'text.secondary',
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 600
              },
              '&:hover': {
                bgcolor: theme => alpha(theme.palette.primary.main, 0.04)
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: 'primary.main',
              height: 3,
              borderRadius: '3px 3px 0 0'
            }
          }}
        >
          <Tab label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Active Hotels
              <Chip 
                label={hotelCounts.active} 
                size="small" 
                color="success"
                sx={{ 
                  height: 20,
                  minWidth: 28,
                  '& .MuiChip-label': {
                    px: 1,
                    fontSize: '0.75rem',
                    fontWeight: 600
                  }
                }}
              />
            </Box>
          } />
          <Tab label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Work In Progress
              <Chip 
                label={hotelCounts.work_in_progress} 
                size="small" 
                color="info"
                sx={{ 
                  height: 20,
                  minWidth: 28,
                  '& .MuiChip-label': {
                    px: 1,
                    fontSize: '0.75rem',
                    fontWeight: 600
                  }
                }}
              />
            </Box>
          } />
          <Tab label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Archived Hotels
              <Chip 
                label={hotelCounts.archived} 
                size="small" 
                color="error"
                sx={{ 
                  height: 20,
                  minWidth: 28,
                  '& .MuiChip-label': {
                    px: 1,
                    fontSize: '0.75rem',
                    fontWeight: 600
                  }
                }}
              />
            </Box>
          } />
        </Tabs>

        {/* Content */}
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            p: 8
          }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ 
                    fontWeight: 600,
                    color: 'text.secondary',
                    fontSize: '0.875rem'
                  }}>
                    Property Number
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600,
                    color: 'text.secondary',
                    fontSize: '0.875rem'
                  }}>
                    Name
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600,
                    color: 'text.secondary',
                    fontSize: '0.875rem'
                  }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600,
                    color: 'text.secondary',
                    fontSize: '0.875rem'
                  }}>
                    Last Updated
                  </TableCell>
                  {currentTab === 1 && (
                    <TableCell sx={{ 
                      fontWeight: 600,
                      color: 'text.secondary',
                      fontSize: '0.875rem'
                    }}>
                      Expected Opening
                    </TableCell>
                  )}
                  <TableCell align="right" sx={{ 
                    fontWeight: 600,
                    color: 'text.secondary',
                    fontSize: '0.875rem'
                  }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredHotels
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((hotel) => (
                  <TableRow 
                    key={hotel.propertyNumber}
                    sx={{
                      '&:hover': {
                        bgcolor: theme => alpha(theme.palette.primary.main, 0.04)
                      }
                    }}
                  >
                    <TableCell sx={{ fontSize: '0.875rem' }}>
                      {hotel.propertyNumber}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem' }}>
                      {hotel.categoryData?.category_name?.value || '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={STATUS_CONFIG[hotel.status].label}
                        color={STATUS_CONFIG[hotel.status].color}
                        size="small"
                        icon={STATUS_CONFIG[hotel.status].icon}
                        sx={{
                          height: 24,
                          '& .MuiChip-label': {
                            px: 1,
                            fontSize: '0.75rem',
                            fontWeight: 500
                          }
                        }}
                      />
                    </TableCell>
                    {currentTab === 1 ? (
                      <>
                        <TableCell sx={{ 
                          fontSize: '0.875rem',
                          color: 'text.secondary'
                        }}>
                          {(hotel.statusHistory?.[0]?.timestamp && typeof hotel.statusHistory[0].timestamp.toDate === 'function' 
                            ? hotel.statusHistory[0].timestamp.toDate().toLocaleDateString() 
                            : hotel.statusHistory?.[0]?.timestamp instanceof Date 
                              ? hotel.statusHistory[0].timestamp.toLocaleDateString()
                              : '-')}
                        </TableCell>
                        <TableCell sx={{ 
                          fontSize: '0.875rem',
                          color: hotel.expectedOpeningDate ? 'text.primary' : 'text.secondary'
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {hotel.expectedOpeningDate ? new Date(hotel.expectedOpeningDate).toLocaleDateString() : 'Not set'}
                            <Tooltip title="Edit expected opening date">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedHotelForEdit(hotel);
                                  setEditedDate(hotel.expectedOpeningDate || '');
                                  setOpenEditDateDialog(true);
                                }}
                                sx={{
                                  color: 'primary.main',
                                  '&:hover': {
                                    bgcolor: theme => alpha(theme.palette.primary.main, 0.08)
                                  }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </>
                    ) : (
                      <TableCell sx={{ 
                        fontSize: '0.875rem',
                        color: 'text.secondary'
                      }}>
                        {(hotel.statusHistory?.[0]?.timestamp && typeof hotel.statusHistory[0].timestamp.toDate === 'function' 
                          ? hotel.statusHistory[0].timestamp.toDate().toLocaleDateString() 
                          : hotel.statusHistory?.[0]?.timestamp instanceof Date 
                            ? hotel.statusHistory[0].timestamp.toLocaleDateString()
                            : '-')}
                      </TableCell>
                    )}
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        {hotel.status === 'active' ? (
                          <>
                            <Tooltip title="Set as Work In Progress">
                              <IconButton
                                onClick={() => handleStatusChange(hotel.propertyNumber, 'work_in_progress')}
                                size="small"
                                sx={{
                                  color: 'info.main',
                                  '&:hover': {
                                    bgcolor: theme => alpha(theme.palette.info.main, 0.08)
                                  }
                                }}
                              >
                                <ConstructionIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Archive Hotel">
                              <IconButton
                                onClick={() => handleStatusChange(hotel.propertyNumber, 'archived')}
                                size="small"
                                sx={{
                                  color: 'error.main',
                                  '&:hover': {
                                    bgcolor: theme => alpha(theme.palette.error.main, 0.08)
                                  }
                                }}
                              >
                                <ArchiveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : hotel.status === 'work_in_progress' ? (
                          <>
                            <Tooltip title="Set as Active">
                              <IconButton
                                onClick={() => handleStatusChange(hotel.propertyNumber, 'active')}
                                size="small"
                                sx={{
                                  color: 'success.main',
                                  '&:hover': {
                                    bgcolor: theme => alpha(theme.palette.success.main, 0.08)
                                  }
                                }}
                              >
                                <RestoreIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Archive Hotel">
                              <IconButton
                                onClick={() => handleStatusChange(hotel.propertyNumber, 'archived')}
                                size="small"
                                sx={{
                                  color: 'error.main',
                                  '&:hover': {
                                    bgcolor: theme => alpha(theme.palette.error.main, 0.08)
                                  }
                                }}
                              >
                                <ArchiveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <>
                            <Tooltip title="Restore Hotel">
                              <IconButton
                                onClick={() => handleStatusChange(hotel.propertyNumber, 'active')}
                                size="small"
                                sx={{
                                  color: 'success.main',
                                  '&:hover': {
                                    bgcolor: theme => alpha(theme.palette.success.main, 0.08)
                                  }
                                }}
                              >
                                <RestoreIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Permanently Delete Hotel">
                              <IconButton
                                onClick={() => {
                                  setHotelToDelete(hotel);
                                  setOpenDeleteDialog(true);
                                }}
                                size="small"
                                sx={{
                                  color: 'error.main',
                                  '&:hover': {
                                    bgcolor: theme => alpha(theme.palette.error.main, 0.08)
                                  }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={filteredHotels.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Hotels per page:"
              sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
                '& .MuiTablePagination-select': {
                  borderRadius: 1
                }
              }}
            />
          </TableContainer>
        )}
        </Box>
      </Card>

      {/* Dialog */}
      <Dialog 
        open={openAddDialog} 
        onClose={() => setOpenAddDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            bgcolor: 'background.paper',
            backgroundImage: 'none',
            maxWidth: '900px',
            width: '100%',
            mx: 2,
            boxShadow: theme => `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 4,
          background: theme => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.light, 0.04)} 100%)`,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: theme => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                color: 'white',
                boxShadow: theme => `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
              }}
            >
              <AddIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box flex={1}>
              <Typography variant="h5" sx={{ 
                fontFamily: 'StrawberrySansDisplay',
                fontWeight: 600,
                mb: 0.5,
                color: 'text.primary'
              }}>
                Add New Hotel
              </Typography>
              <Typography variant="body2" sx={{ 
                color: 'text.secondary',
                lineHeight: 1.5
              }}>
                Fill in the required information below. Additional details can be added later.
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 4 }}>
            {/* Basic Information Section */}
            <Box sx={{ 
              mb: 4,
              p: 3,
              borderRadius: 2,
              bgcolor: theme => alpha(theme.palette.primary.main, 0.02),
              border: '1px solid',
              borderColor: theme => alpha(theme.palette.primary.main, 0.1)
            }}>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  mb: 2.5,
                  fontWeight: 600,
                  color: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  textTransform: 'uppercase',
                  fontSize: '0.75rem',
                  letterSpacing: '0.5px'
                }}
              >
                <Box sx={{ 
                  width: 4, 
                  height: 16, 
                  bgcolor: 'primary.main',
                  borderRadius: 1
                }} />
                Basic Information
              </Typography>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Property Number"
                    fullWidth
                    value={newHotel.propertyNumber}
                    onChange={(e) => setNewHotel(prev => ({ ...prev, propertyNumber: e.target.value }))}
                    required
                    helperText="Enter proterty number in lowercase ⚠️"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'background.paper',
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main'
                          }
                        }
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Initial Status</InputLabel>
                    <Select
                      value={newHotel.status || 'active'}
                      onChange={(e) => setNewHotel(prev => ({ ...prev, status: e.target.value }))}
                      label="Initial Status"
                      sx={{
                        bgcolor: 'background.paper',
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'primary.main'
                          }
                        }
                      }}
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="work_in_progress">Work In Progress</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {newHotel.status === 'work_in_progress' && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Expected Opening Date"
                      type="date"
                      fullWidth
                      value={newHotel.expectedOpeningDate || ''}
                      onChange={(e) => setNewHotel(prev => ({ ...prev, expectedOpeningDate: e.target.value }))}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: 'background.paper'
                        }
                      }}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
            
            {/* Dynamic Category Groups */}
            {categories.map((group, groupIndex) => {
              // Filter to show only required fields that are not archived and not property number
              const requiredItems = group.items.filter(item => 
                !item.isArchived && 
                item.isRequired && 
                item.fieldName.toLowerCase() !== 'propertynumber'
              );
              
              // Debug logging
              if (process.env.NODE_ENV === 'development') {
                console.log(`Group: ${group.title}, Required items:`, requiredItems.map(i => `${i.title} (required: ${i.isRequired})`));
              }
              
              if (requiredItems.length === 0) return null;

              // Color scheme for different groups
              const groupColors = [
                { main: '#1976d2', light: '#42a5f5', bg: alpha('#1976d2', 0.02) },
                { main: '#9c27b0', light: '#ba68c8', bg: alpha('#9c27b0', 0.02) },
                { main: '#f57c00', light: '#ff9800', bg: alpha('#f57c00', 0.02) },
                { main: '#388e3c', light: '#66bb6a', bg: alpha('#388e3c', 0.02) },
              ];
              const colorScheme = groupColors[groupIndex % groupColors.length];

              return (
                <Box 
                  key={group.id} 
                  sx={{ 
                    mb: 3,
                    p: 3,
                    borderRadius: 2,
                    bgcolor: colorScheme.bg,
                    border: '1px solid',
                    borderColor: alpha(colorScheme.main, 0.1),
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: alpha(colorScheme.main, 0.2),
                      boxShadow: `0 2px 8px ${alpha(colorScheme.main, 0.08)}`
                    }
                  }}
                >
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      mb: 2.5,
                      fontWeight: 600,
                      color: colorScheme.main,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem',
                      letterSpacing: '0.5px'
                    }}
                  >
                    <Box sx={{ 
                      width: 4, 
                      height: 16, 
                      bgcolor: colorScheme.main,
                      borderRadius: 1
                    }} />
                    {group.title}
                    <Chip 
                      label={`${requiredItems.length} required`}
                      size="small"
                      sx={{
                        ml: 'auto',
                        height: 20,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        bgcolor: alpha(colorScheme.main, 0.1),
                        color: colorScheme.main,
                        border: `1px solid ${alpha(colorScheme.main, 0.2)}`
                      }}
                    />
                  </Typography>
                  <Grid container spacing={2.5}>
                    {requiredItems
                      // Filter out password fields if user doesn't have permission
                      // Admins can always view password fields
                      .filter(item => !(item.type === 'password' && 
                                        user?.role !== 'admin' && 
                                        !(user?.permissions || []).includes('view-passwords')))
                      .map(item => (
                      <Grid item xs={12} sm={6} key={item.id}>
                        <TextField
                          fullWidth
                          label={item.title}
                          value={newHotel.categoryData[`category_${item.fieldName.toLowerCase()}`]?.value || ''}
                          onChange={(e) => handleCategoryChange(item.fieldName, e.target.value)}
                          type="text"
                          required
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              bgcolor: 'background.paper',
                              '&:hover': {
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: colorScheme.main
                                }
                              },
                              '&.Mui-focused': {
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: colorScheme.main
                                }
                              }
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                              color: colorScheme.main
                            }
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: 3,
          gap: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider'
        }}>
          <Button 
            onClick={() => setOpenAddDialog(false)}
            size="large"
            sx={{
              px: 3,
              py: 1,
              borderRadius: 2,
              color: 'text.secondary',
              fontWeight: 500,
              textTransform: 'none',
              fontSize: '0.9375rem',
              '&:hover': {
                bgcolor: theme => alpha(theme.palette.common.black, 0.06),
                color: 'text.primary'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddHotel}
            variant="contained"
            size="large"
            disabled={!newHotel.propertyNumber}
            startIcon={<AddIcon />}
            sx={{
              px: 4,
              py: 1,
              borderRadius: 2,
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '0.9375rem',
              background: theme => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              boxShadow: theme => `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                background: theme => `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                boxShadow: theme => `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                transform: 'translateY(-1px)'
              },
              '&:disabled': {
                background: theme => alpha(theme.palette.action.disabledBackground, 0.12),
                color: theme => alpha(theme.palette.action.disabled, 0.38)
              },
              transition: 'all 0.2s ease'
            }}
          >
            Add Hotel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Date Dialog */}
      <Dialog 
        open={openEditDateDialog} 
        onClose={() => {
          setOpenEditDateDialog(false);
          setSelectedHotelForEdit(null);
          setEditedDate('');
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'background.paper',
            backgroundImage: 'none'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: theme => alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main'
            }}
          >
            <EditIcon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ 
              fontFamily: 'StrawberrySansDisplay',
              fontWeight: 500,
              mb: 0.5
            }}>
              Edit Expected Opening Date
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedHotelForEdit?.categoryData?.category_name?.value || 'Hotel'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <TextField
            label="Expected Opening Date"
            type="date"
            fullWidth
            value={editedDate}
            onChange={(e) => setEditedDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </DialogContent>
        <DialogActions sx={{ 
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider'
        }}>
          <Button 
            onClick={() => {
              setOpenEditDateDialog(false);
              setSelectedHotelForEdit(null);
              setEditedDate('');
            }}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                bgcolor: theme => alpha(theme.palette.common.black, 0.04)
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateExpectedOpeningDate}
            variant="contained"
            sx={{
              px: 3,
              bgcolor: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.dark'
              }
            }}
          >
            Update Date
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={openDeleteDialog} 
        onClose={() => {
          setOpenDeleteDialog(false);
          setHotelToDelete(null);
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'background.paper',
            backgroundImage: 'none'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: theme => alpha(theme.palette.error.main, 0.1),
              color: 'error.main'
            }}
          >
            <DeleteIcon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ 
              fontFamily: 'StrawberrySansDisplay',
              fontWeight: 500,
              mb: 0.5
            }}>
              Permanently Delete Hotel
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This action cannot be undone
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to permanently delete this hotel?
          </Typography>
          <Box sx={{ 
            p: 2, 
            borderRadius: 2, 
            bgcolor: theme => alpha(theme.palette.error.main, 0.08),
            border: '1px solid',
            borderColor: theme => alpha(theme.palette.error.main, 0.2)
          }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Hotel: {hotelToDelete?.categoryData?.category_name?.value || hotelToDelete?.propertyNumber}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Property Number: {hotelToDelete?.propertyNumber}
            </Typography>
          </Box>
          <Typography variant="body2" color="error.main" sx={{ mt: 2, fontWeight: 500 }}>
            ⚠️ This will permanently remove all hotel data from the system.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ 
          p: 2,
          gap: 1,
          borderTop: '1px solid',
          borderColor: 'divider'
        }}>
          <Button 
            onClick={() => {
              setOpenDeleteDialog(false);
              setHotelToDelete(null);
            }}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                bgcolor: theme => alpha(theme.palette.common.black, 0.04)
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteHotel}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            sx={{
              px: 3,
              '&:hover': {
                bgcolor: 'error.dark'
              }
            }}
          >
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <style jsx global>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </PageContent>
  );
};

export default HotelManagement; 